import {
  WSCard,
  WSEmptyState,
  WSFlexBox
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useUserId } from "../../../../query/hooks/helpers";
import { usePayoutSettings } from "../../../../query/payments/queries";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorDefaultDestination } from "../../../../shared/selectors/selectorDefaultDestination";
import { BadgePayoutPreference } from "../BadgePayoutPreference";
import {
  PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD,
  PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD
} from "../../../Settings/routes/RoutePaymentMethods";
import { LabelMethod } from "../LabelMethod";
import { MethodType } from "../ManageMethods/types";

type Props = { noDescription?: boolean };

export const DefaultPayoutMethod: React.FC<Props> = ({ noDescription }) => {
  const userId = useUserId();
  const queryPayoutSettings = usePayoutSettings(userId);
  const history = useHistory();
  const defaultPayoutMethod = useDefaultPayoutMethod();

  return (
    <WSCard
      shimmer={queryPayoutSettings.isLoading}
      divider={!noDescription}
      header={{
        label: {
          text: "Payout method",
          helperText: noDescription
            ? undefined
            : "Default method to receive payments from clients"
        },
        value: noDescription
          ? undefined
          : {
              pill: {
                theme: "neutral",
                text: "Default",
                badge: true
              }
            }
      }}
      actions={{
        alignment: "left",
        buttons: [
          {
            kind: "Link",
            label: "Manage payout method",
            rightIcon: "chevron-right",
            onClick: () => {
              history.push(PATH_SETTINGS_PAYMENT_METHODS_MANAGE_PAYOUT_METHOD);
            }
          }
        ]
      }}
    >
      {!!defaultPayoutMethod ? (
        <WSFlexBox justify="space-between" alignItems="center" wrap="nowrap">
          <LabelMethod
            type={defaultPayoutMethod.type}
            id={defaultPayoutMethod.id}
          />
          {queryPayoutSettings.data && (
            <BadgePayoutPreference
              payoutPreference={queryPayoutSettings.data?.payoutPreferences}
            />
          )}
        </WSFlexBox>
      ) : (
        <WSEmptyState
          type="card"
          title="No payout method added"
          orientation="horizontal"
          description="Add a default payout method to get paid."
          size="S"
          actions={{
            buttons: [
              {
                label: "Add payout method",
                kind: "Secondary",
                onClick: () => {
                  history.push(PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYOUT_METHOD);
                }
              }
            ]
          }}
        />
      )}
    </WSCard>
  );
};

export function payoutDestinationTypeToMethodType(
  destinationType: PayoutDestinationType
): MethodType | undefined {
  switch (destinationType) {
    case PayoutDestinationType.Account:
      return "Account";
    case PayoutDestinationType.Card:
      return "CheckbookCard";
    case PayoutDestinationType.PaymentCard:
      return "PaymentCard";
    default:
      return undefined;
  }
}

export const useDefaultPayoutMethod = () => {
  const userId = useUserId();
  const queryPayoutSettings = usePayoutSettings(userId);
  const queryInternalAccounts = useQueryInternalAccounts();

  return useMemo(():
    | {
        type: MethodType;
        id: string;
        category: PayoutPreferences;
      }
    | undefined => {
    const payoutSettings = queryPayoutSettings.data;

    if (!payoutSettings) {
      return;
    }

    const destination = selectorDefaultDestination(payoutSettings);

    // If no payout destination is set, check if there is Wallet
    if (!destination) {
      const bankingInternalAccount = queryInternalAccounts.data?.find(
        selectorActiveBankingAccount
      );

      if (!bankingInternalAccount) {
        return;
      }

      return {
        type: "InternalAccount",
        id: bankingInternalAccount.internalAccountId,
        category: payoutSettings.payoutPreferences || PayoutPreferences.Standard
      };
    }

    switch (destination.destinationType) {
      case PayoutDestinationType.Account:
        return {
          type: "Account",
          id: destination.destinationId,
          category: destination.payoutMethod
        };

      case PayoutDestinationType.Card:
        return {
          type: "CheckbookCard",
          id: destination.destinationId,
          category: destination.payoutMethod
        };

      case PayoutDestinationType.PaymentCard:
        return {
          type: "PaymentCard",
          id: destination.destinationId,
          category: destination.payoutMethod
        };

      case "InternalAccount" as PayoutDestinationType:
        return {
          type: "InternalAccount",
          id: destination.destinationId,
          category: destination.payoutMethod
        };

      default:
        return;
    }
  }, [queryInternalAccounts.data, queryPayoutSettings.data]);
};
